import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnError, HasOnSuccess, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import applyChanges from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ApplyChanges";
import changePickUpWarehouse from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ChangePickUpWarehouse";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import WarehouseAddressInfoDisplay, {
    WarehouseAddressInfoDisplayStyles,
} from "@insite/content-library/Components/WarehouseAddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSafeRedirectUrl } from "@WhiteCap/Common/Utillities/SafeRedirect";
import changeShipWarehouse from "@WhiteCap/Handlers/WarehouseHandler/ChangeShipWarehouse";
import WarehouseModalLocationCall from "@WhiteCap/Icons/WarehouseModalLocationCall";
import GoogleMapsDirectionLink from "@WhiteCap/Overrides/Components/GoogleMapsDirectionLink";
import WarehouseHoursLink from "@WhiteCap/Overrides/Components/WarehouseHoursLink";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import trim from "lodash/trim";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => ({
    fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
    pickUpWarehouse: state.components.addressDrawer.pickUpWarehouse,
    changeCustomerWarehouse: state.WhiteCap.changeCustomerWarehouseData,
    sessionWarehouse: state.context.session.pickUpWarehouse,
    pageType: getCurrentPage(state).type,
    homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
});

const mapDispatchToProps = {
    changePickUpWarehouse,
    changeShipWarehouse,
    applyChanges: makeHandlerChainAwaitable(applyChanges),
    setDrawerIsOpen,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasOnSuccess &
    HasOnError;

export interface AddressDrawerPickUpLocationSelectorStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    changeLink?: ButtonPresentationProps;
    warehouseAddress?: AddressInfoCondensedDisplayStyles;
    warehousePhoneGridItem?: GridItemProps;
    warehousePhoneText?: TypographyPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    container?: GridContainerProps;
    buttonContainer?: GridContainerProps;
    warehouseAddressGridItem?: GridItemProps;
    selectedWarehouseLinksGridItem?: GridItemProps;
    selectedWarehouseLink?: LinkPresentationProps;
    pipeSeparatorTypography?: TypographyPresentationProps;
    selectedWarehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    warehouseHoursModal?: ModalPresentationProps;
    hoursContainer?: InjectableCss;
    searchResultPhoneLink?: LinkPresentationProps;
    locationPhoneIcon?: IconPresentationProps;
    locationHoursIcon?: IconPresentationProps;
    locationDirectionsIcon?: IconPresentationProps;
    warehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    warehouseHoursLink?: LinkPresentationProps;
}

export const addressDrawerPickUpLocationSelectorStyles: AddressDrawerPickUpLocationSelectorStyles = {
    headerText: {
        weight: 600,
        css: css`
            margin-bottom: 8px;
        `,
    },
    warehousePhoneGridItem: {
        width: 12,
        css: css`
            padding: 0px;
            padding-bottom: 16px;
        `,
    },
    warehousePhoneText: {
        css: css`
            color: ${getColor("text.link")};
            font-size: 12px;
        `,
    },
    changeLink: {
        variant: "secondary",
        buttonType: "outline",
        typographyProps: {
            css: css`
                font-size: 14px;
                font-weight: 450;
                line-height: 24px;
                text-transform: uppercase;
            `,
        },
        css: css`
            width: 100%;
        `,
    },
    container: {
        css: css`
            > div[class^="GridOffset"] {
                padding: 16px 24px;
            }
        `,
    },

    warehouseAddressGridItem: {
        width: 12,
        css: css`
            display: block;
            padding: 8px 8px 12px 8px;
            background-color: #f7f8f9;
            height: 140px;
            align-items: center;
        `,
    },
    warehouseAddress: {
        wrapper: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    selectedWarehouseLinksGridItem: {
        width: 6,
        css: css`
            padding-top: 0px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    pipeSeparatorTypography: {
        css: css`
            margin-left: 10px;
            margin-right: 10px;
        `,
    },
    warehouseHoursModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    hoursContainer: {
        css: css`
            white-space: pre-wrap;
        `,
    },
    locationPhoneIcon: {
        size: 30,
        css: css`
            margin-top: 15px;
            margin-right: 24px;
        `,
    },
    locationDirectionsIcon: {
        size: 30,
        css: css`
            margin-top: 15px;
            margin-right: 24px;
        `,
    },
    locationHoursIcon: {
        size: 30,
        css: css`
            margin-top: 15px;
            margin-right: 24px;
        `,
    },
};

const styles = addressDrawerPickUpLocationSelectorStyles;

const AddressDrawerPickUpLocationSelector = ({
    fulfillmentMethod,
    pickUpWarehouse,
    changePickUpWarehouse,
    changeCustomerWarehouse,
    changeShipWarehouse,
    applyChanges,
    setDrawerIsOpen,
    toaster,
    sessionWarehouse,
    pageType,
    homePageUrl,
}: Props) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [modalKey, setModalKey] = React.useState(0);
    const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseModel | undefined>(undefined);
    const [warehouseHoursToDisplayModalOpen, setWarehouseHoursToDisplayModalOpen] = useState<boolean>(false);
    const [warehouseHoursToDisplay, setWarehouseHoursToDisplay] = useState<WarehouseModel | undefined>(undefined);

    useEffect(() => {
        setSelectedWarehouse(
            changeCustomerWarehouse?.Warehouse
                ? changeCustomerWarehouse.Warehouse
                : pickUpWarehouse || sessionWarehouse || undefined,
        );
    }, [pickUpWarehouse, changeCustomerWarehouse?.Warehouse, sessionWarehouse]);

    const handleChangeLocationClick = () => {
        setModalKey(prev => prev + 1);
        setIsModalOpen(true);
    };
    const handleModalClose = () => setIsModalOpen(false);
    const handleWarehouseSelected = async (warehouse: WarehouseModel) => {
        changePickUpWarehouse({ warehouse });
        changeShipWarehouse({ warehouse });
        await applyChanges({
            onError: (errorMessage: string) => {
                toaster.addToast({
                    messageType: "warning",
                    body: siteMessage("ChangeCustomer_Update_Failed", errorMessage),
                });
            },
        });
        setDrawerIsOpen({ isOpen: false });
        setIsModalOpen(false);
        if (pageType === "ChangeCustomerPage") {
            window.location.href = getSafeRedirectUrl(homePageUrl);
        }
    };

    const handleOpenHoursModal = (warehouse: WarehouseModel) => {
        setWarehouseHoursToDisplayModalOpen(!!warehouse.hours);
        setWarehouseHoursToDisplay(warehouse);
    };

    const handleHoursModalClose = () => {
        setWarehouseHoursToDisplayModalOpen(false);
        setWarehouseHoursToDisplay(undefined);
    };

    const generateOpenWarehouseHoursFor = (warehouse: WarehouseModel) => {
        return () => handleOpenHoursModal(warehouse);
    };

    return (
        <>
            <StyledWrapper
                id="addressDrawerPickUpLocationSelector"
                role="region"
                aria-live="polite"
                data-test-selector="changeCustomer_pickUpLocation"
                {...styles.wrapper}
            >
                {fulfillmentMethod === FulfillmentMethod.PickUp || fulfillmentMethod === FulfillmentMethod.Ship ? (
                    <>
                        <GridContainer {...styles.container}>
                            <GridItem width={12} style={{ padding: "0px" }}>
                                <Typography {...styles.headerText}>
                                    {fulfillmentMethod === FulfillmentMethod.PickUp
                                        ? translate("Pick Up Address")
                                        : translate("Selected Branch")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.warehouseAddressGridItem}>
                                {selectedWarehouse && (
                                    <>
                                        <Typography
                                            as="p"
                                            style={{ fontWeight: "bold", paddingBottom: "6px", fontSize: "14px" }}
                                        >{`${selectedWarehouse?.city} ${selectedWarehouse?.name}`}</Typography>
                                        <AddressInfoCondensedDisplay
                                            {...selectedWarehouse}
                                            extendedStyles={styles.warehouseAddress}
                                        />
                                    </>
                                )}
                                <Hidden below="lg">
                                    {selectedWarehouse?.phone && (
                                        <GridItem {...styles.warehousePhoneGridItem}>
                                            <Typography {...styles.warehousePhoneText}>
                                                {selectedWarehouse?.phone}
                                            </Typography>
                                        </GridItem>
                                    )}
                                    <StyledWrapper style={{ fontSize: "12px" }}>
                                        {selectedWarehouse && (
                                            <WarehouseHoursLink
                                                {...styles.selectedWarehouseLink}
                                                warehouse={selectedWarehouse}
                                                onOpenWarehouseHours={handleOpenHoursModal}
                                            />
                                        )}
                                        <Typography {...styles.pipeSeparatorTypography}>|</Typography>
                                        <GoogleMapsDirectionLink
                                            {...styles.selectedWarehouseGoogleMapsDirectionLink}
                                            address1={
                                                changeCustomerWarehouse?.Warehouse?.address1 ??
                                                (pickUpWarehouse?.address1 ? pickUpWarehouse?.address1 : "")
                                            }
                                            city={
                                                changeCustomerWarehouse?.Warehouse?.city ??
                                                (pickUpWarehouse?.city ? pickUpWarehouse?.city : "")
                                            }
                                            state={
                                                changeCustomerWarehouse?.Warehouse?.state ??
                                                (pickUpWarehouse?.state ? pickUpWarehouse?.state : "")
                                            }
                                            postalCode={
                                                changeCustomerWarehouse?.Warehouse?.postalCode ??
                                                (pickUpWarehouse?.postalCode ? pickUpWarehouse?.postalCode : "")
                                            }
                                            {...selectedWarehouse}
                                        />
                                    </StyledWrapper>
                                </Hidden>
                                <Hidden above="sm" style={{ maxWidth: "max-content" }}>
                                    <StyledWrapper style={{ display: "flex" }}>
                                        {selectedWarehouse?.phone && (
                                            <Link
                                                href={`tel:${selectedWarehouse?.phone} `}
                                                {...styles.searchResultPhoneLink}
                                                target="_blank"
                                            >
                                                <Icon
                                                    {...styles.locationPhoneIcon}
                                                    src={WarehouseModalLocationCall}
                                                ></Icon>
                                            </Link>
                                        )}
                                        {selectedWarehouse?.hours && (
                                            <WarehouseHoursLink
                                                {...styles.warehouseHoursLink}
                                                iconMode={true}
                                                iconStyleProps={styles.locationHoursIcon}
                                                warehouse={selectedWarehouse}
                                                onOpenWarehouseHours={generateOpenWarehouseHoursFor(selectedWarehouse)}
                                            />
                                        )}
                                        <GoogleMapsDirectionLink
                                            {...styles.warehouseGoogleMapsDirectionLink}
                                            iconMode={true}
                                            iconStyles={styles.locationDirectionsIcon}
                                            address1={
                                                changeCustomerWarehouse?.Warehouse?.address1 ??
                                                (pickUpWarehouse?.address1 ? pickUpWarehouse?.address1 : "")
                                            }
                                            city={
                                                changeCustomerWarehouse?.Warehouse?.city ??
                                                (pickUpWarehouse?.city ? pickUpWarehouse?.city : "")
                                            }
                                            state={
                                                changeCustomerWarehouse?.Warehouse?.state ??
                                                (pickUpWarehouse?.state ? pickUpWarehouse?.state : "")
                                            }
                                            postalCode={
                                                changeCustomerWarehouse?.Warehouse?.postalCode ??
                                                (pickUpWarehouse?.postalCode ? pickUpWarehouse?.postalCode : "")
                                            }
                                            {...selectedWarehouse}
                                        />
                                    </StyledWrapper>
                                </Hidden>
                            </GridItem>
                        </GridContainer>
                        <GridContainer {...styles.buttonContainer}>
                            <GridItem width={12} style={{ padding: "8px 20px" }}>
                                <Button
                                    onClick={handleChangeLocationClick}
                                    data-test-selector="changeCustomer_pickUpLocation_findLocation"
                                    {...styles.changeLink}
                                >
                                    {translate("Change Branch")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                        <Modal
                            {...styles.warehouseHoursModal}
                            headline={translate("Hours")}
                            isOpen={warehouseHoursToDisplayModalOpen}
                            handleClose={handleHoursModalClose}
                        >
                            {warehouseHoursToDisplay?.hours && (
                                <StyledWrapper {...styles.hoursContainer}>
                                    <table cellPadding={0} style={{ width: 260 }}>
                                        <tbody>
                                            {JSON.parse(warehouseHoursToDisplay?.hours).map((time: any) => (
                                                // eslint-disable-next-line react/jsx-key
                                                <tr>
                                                    <td>
                                                        <span>
                                                            <strong>{trim(time?.WeekDay)}</strong>
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span>{trim(time?.Hours)}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </StyledWrapper>
                            )}
                        </Modal>
                    </>
                ) : (
                    <></>
                )}
            </StyledWrapper>
            <FindLocationModal
                modalIsOpen={isModalOpen}
                key={modalKey}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleModalClose}
                extendedStyles={styles.findLocationModal}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(AddressDrawerPickUpLocationSelector)),
    definition: {
        group: "Header",
        icon: "store",
    },
};

export default widgetModule;
